import { useEffect, useMemo, useRef, useState } from "react"
import { useIdleTimer } from "react-idle-timer"
import {
  getAccessToken,
  getAuthenticatedUser,
  initializeKeycloak,
  loginWithKeycloak,
  logoutFromKeycloak,
  SIGNING_OUT_QUERY_PARAM,
  shouldRedirectToKeycloakLogin,
  type AuthenticatedUser,
} from "@/common/keycloak/keycloak"
import { Login } from "./login/Login"
import { SignedOut } from "./login/SignedOut"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/common/elements/sidebar"
import { TooltipProvider } from "@/common/elements/tooltip"
import { AppSidebar } from "@/app/sidebar/Sidebar"
import { Separator } from "@/common/elements/separator"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { AuthStatus, ApiSession } from "@/common/auth/utils"
import { pageComponents } from "@/pages"
import { routes } from "@/config/routes"
import { Page } from "@/config/models"
import { usePermissions } from "@/common/hooks/use-permissions"
import { useGetHarnessCustomersQuery, type HarnessCustomer } from "@/api"
import { setSelectedCustomerId } from "@/store/billing-customer"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { BootstrapScreen } from "@/app/bootstrap-screen/BootstrapScreen"
import {
  buildUrlWithoutQueryParam,
  getCurrentBrowserHref,
  getMatchingRoute,
  getNumericHttpStatus,
  hasQueryParamValue,
  replaceBrowserUrl,
  setBrowserDocumentTitle,
} from "@/common/utils/routeUtils"

type SessionConfig = {
  idleTimeoutMinutes: number
  idlePromptMinutes: number
}

// for consistency keep default up to date with the defult in `cloudfit-portal\src\src\backend\Api\Controllers\Config\ConfigController.cs`
// this default is a backup and should only ever be used should the backend fail to provide a value for the session config
const defaultSessionConfig: SessionConfig = {
  idleTimeoutMinutes: 15,
  idlePromptMinutes: 5,
}

function resolveSessionConfig(config: Partial<SessionConfig>): SessionConfig {
  const timeoutValue = Number(config.idleTimeoutMinutes)
  const promptValue = Number(config.idlePromptMinutes)

  const idleTimeoutMinutes = Number.isFinite(timeoutValue) && timeoutValue > 0
    ? timeoutValue
    : defaultSessionConfig.idleTimeoutMinutes

  const rawPromptMinutes = Number.isFinite(promptValue) && promptValue >= 0
    ? promptValue
    : defaultSessionConfig.idlePromptMinutes

  const idlePromptMinutes = rawPromptMinutes < idleTimeoutMinutes
    ? rawPromptMinutes
    : Math.max(idleTimeoutMinutes - 1, 0)

  return {
    idleTimeoutMinutes,
    idlePromptMinutes,
  }
}

export function App() {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [authStatus, setAuthStatus] = useState<AuthStatus>("loading")
  const [authUser, setAuthUser] = useState<AuthenticatedUser | null>(null)
  const [apiSession, setApiSession] = useState<ApiSession | null>(null)
  const [loginInFlight, setLoginInFlight] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [sessionConfig, setSessionConfig] = useState<SessionConfig>(defaultSessionConfig)
  const [isSessionTimeoutPromptOpen, setSessionTimeoutPromptOpen] = useState(false)
  const [redirectToKeycloak, setRedirectToKeycloak] = useState(false)
  const [isRedirectingToKeycloak, setIsRedirectingToKeycloak] = useState(false)
  const [isSignedOut, setIsSignedOut] = useState(false)
  const loginRedirectInFlight = useRef(false)
  const selectedCustomerId = useAppSelector((state) => state.billingCustomer.selectedCustomerId)
  const isSigningOut = useMemo(() => {
    return hasQueryParamValue(location.search, SIGNING_OUT_QUERY_PARAM, "1")
  }, [location.search])

  const { hasPagePermission } = usePermissions()

  const shouldPreloadCustomers = authStatus === "authenticated" && authUser !== null
  const {
    data: customersPage,
    error: customersError,
    isError: isCustomersError,
    isLoading: isCustomersLoading,
    isSuccess: isCustomersSuccess,
    isUninitialized: isCustomersUninitialized,
  } = useGetHarnessCustomersQuery(undefined, { skip: !shouldPreloadCustomers })

  const customers = useMemo<HarnessCustomer[]>(() => customersPage?.data ?? [], [customersPage])
  const hasValidSelectedCustomer = selectedCustomerId !== null && customers.some((customer) => customer.id === selectedCustomerId)

  useEffect(() => {
    if (!shouldPreloadCustomers || !isCustomersSuccess) {
      return
    }

    if (customers.length === 0) {
      if (selectedCustomerId !== null) {
        dispatch(setSelectedCustomerId(null))
      }
      return
    }

    if (!hasValidSelectedCustomer) {
      dispatch(setSelectedCustomerId(customers[0].id))
    }
  }, [
    customers,
    dispatch,
    hasValidSelectedCustomer,
    isCustomersSuccess,
    selectedCustomerId,
    shouldPreloadCustomers,
  ])

  useEffect(() => {
    if (authStatus === "loading" || !isSigningOut) {
      return
    }

    const nextUrl = buildUrlWithoutQueryParam(getCurrentBrowserHref(), SIGNING_OUT_QUERY_PARAM)
    replaceBrowserUrl(nextUrl)
  }, [authStatus, isSigningOut])

  useEffect(() => {
    const baseTitle = "CloudFit Portal"

    if (authStatus === "loading") {
      setBrowserDocumentTitle(baseTitle)
      return
    }

    if (authStatus !== "authenticated" || !authUser) {
      setBrowserDocumentTitle(`${baseTitle} - Sign In`)
      return
    }

    if (isCustomersUninitialized || isCustomersLoading || (customers.length > 0 && !hasValidSelectedCustomer)) {
      setBrowserDocumentTitle(baseTitle)
      return
    }

    if (isCustomersError) {
      setBrowserDocumentTitle(baseTitle)
      return
    }

    if (isCustomersSuccess && customers.length === 0) {
      setBrowserDocumentTitle(baseTitle)
      return
    }

    const matchingRoute = getMatchingRoute(location.pathname, routes)

    setBrowserDocumentTitle(`${baseTitle} - ${matchingRoute?.pageTitle ?? "Home"}`)
  }, [
    authStatus,
    authUser,
    customers,
    customersError,
    hasValidSelectedCustomer,
    isCustomersError,
    isCustomersLoading,
    isCustomersSuccess,
    isCustomersUninitialized,
    isSigningOut,
    location.pathname,
  ])

  useEffect(() => {
    let cancelled = false

    async function hydrateAuth() {
      try {
        const keycloak = await initializeKeycloak()

        if (cancelled) {
          return
        }

        if (!keycloak.authenticated) {
          setAuthUser(null)
          setApiSession(null)
          setAuthError(null)
          const shouldRedirect = shouldRedirectToKeycloakLogin()
          setRedirectToKeycloak(shouldRedirect)
          setIsSignedOut(isSigningOut)
          setAuthStatus("anonymous")

          if (shouldRedirect && !isSigningOut && !loginRedirectInFlight.current) {
            loginRedirectInFlight.current = true
            setIsRedirectingToKeycloak(true)

            try {
              await loginWithKeycloak("")
            } catch (error) {
              loginRedirectInFlight.current = false

              if (!cancelled) {
                setIsRedirectingToKeycloak(false)
                setAuthStatus("error")
                setAuthError(error instanceof Error ? error.message : "The login redirect could not be started.")
              }
            }
          }

          return
        }

        setIsRedirectingToKeycloak(false)
        const user = await getAuthenticatedUser(keycloak)
        setAuthUser(user)
        setAuthError(null)
        setAuthStatus("authenticated")

        const token = await getAccessToken()
        if (!token || cancelled) {
          return
        }

        const response = await fetch("/api/auth/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`The API rejected the current access token (${response.status}).`)
        }

        const session = await response.json() as ApiSession

        if (!cancelled) {
          setApiSession(session)
        }
      } catch (error) {
        if (!cancelled) {
          setApiSession(null)
          setAuthUser(null)
          setAuthStatus("error")
          setAuthError(error instanceof Error ? error.message : "Keycloak authentication could not be initialized.")
        }
      }
    }

    void hydrateAuth()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function hydrateSessionConfig() {
      try {
        const response = await fetch("/api/config")
        if (!response.ok) {
          return
        }

        const config = await response.json() as Partial<SessionConfig>

        if (!cancelled) {
          setSessionConfig(resolveSessionConfig(config))
        }
      } catch {
        // Defaults are already applied by state initialization.
      }
    }

    void hydrateSessionConfig()

    return () => {
      cancelled = true
    }
  }, [])

  const handleLogin = async (email: string) => {
    setLoginInFlight(true)
    setAuthError(null)

    try {
      await loginWithKeycloak(email)
    } catch (error) {
      setLoginInFlight(false)
      setAuthStatus("error")
      setAuthError(error instanceof Error ? error.message : "The login redirect could not be started.")
    }
  }

  const handleLogout = async () => {
    await logoutFromKeycloak({ includeSigningOutParam: true })
  }

  function openSessionTimeoutModal(): void {
    setSessionTimeoutPromptOpen(true)
  }

  function closeSessionTimeoutModal(): void {
    setSessionTimeoutPromptOpen(false)
  }

  function staySignedIn(): void {
    if (timer.getRemainingTime() > 0) {
      timer.activate()
      closeSessionTimeoutModal()
      return
    }

    void handleLogout()
  }

  const isIdleTimerEnabled = authStatus === "authenticated" && authUser !== null && !isSigningOut

  const timer = useIdleTimer({
    timeout: sessionConfig.idleTimeoutMinutes * 60 * 1000,
    promptBeforeIdle: sessionConfig.idlePromptMinutes * 60 * 1000,
    disabled: !isIdleTimerEnabled,
    crossTab: true,
    onPrompt: () => openSessionTimeoutModal(),
    onIdle: () => {
      closeSessionTimeoutModal()
      void handleLogout()
    },
  })

  useEffect(() => {
    if (!isIdleTimerEnabled) {
      closeSessionTimeoutModal()
    }
  }, [isIdleTimerEnabled])

  if (authStatus === "loading") {
    return (
      <BootstrapScreen
        variant="loading"
        loadingHeading={isSigningOut ? "Signing out of CloudFit Portal..." : undefined}
      />
    )
  }

  if (isRedirectingToKeycloak) {
    return <BootstrapScreen variant="loading" loadingHeading="Redirecting to secure sign in..." />
  }

  if (authStatus !== "authenticated" || !authUser) {
    if (redirectToKeycloak && isSignedOut) {
      return <SignedOut onSignIn={() => handleLogin("")} isLoading={loginInFlight} errorMessage={authError} />
    }

    return <Login onLogin={handleLogin} isLoading={loginInFlight} errorMessage={authError} />
  }

  if (isCustomersUninitialized || isCustomersLoading || (customers.length > 0 && !hasValidSelectedCustomer)) {
    return <BootstrapScreen variant="loading" />
  }

  if (isCustomersError) {
    if (getNumericHttpStatus(customersError) === 401) {
      return <BootstrapScreen variant="unauthorized" onLogout={handleLogout} />
    }

    return <BootstrapScreen variant="unavailable" onLogout={handleLogout} />
  }

  if (isCustomersSuccess && customers.length === 0) {
    return <BootstrapScreen variant="no-organizations" onLogout={handleLogout} />
  }

  const availableRoutes = Object.entries(routes).filter(([key, _definition]) => hasPagePermission(key as Page))
  
  if (availableRoutes.length === 0) {
    return "You do not have any permissions to view data in CloudFit Portal.  If you believe this is an error, please contact your administrator.";
  }
  
  const defaultRoute = availableRoutes.sort(([, defA], [, defB]) => (defA.defaultRouteOrder || 0) - (defB.defaultRouteOrder || 0))[0]

  return (
    <>
      {isSessionTimeoutPromptOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold">Session is about to time out</h2>
            <p className="mt-2 text-sm text-gray-700">
              Stay signed in to keep working, or sign out now.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  closeSessionTimeoutModal()
                  void handleLogout()
                }}
              >
                Sign out
              </button>
              <button
                type="button"
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                onClick={staySignedIn}
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TooltipProvider>
        <SidebarProvider>
          <AppSidebar
            customers={customers}
            onLogout={handleLogout}
            user={{
              name: authUser.name,
              email: authUser.email,
              avatar: authUser.avatar,
            }}
          />
          <SidebarInset>
            <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <h1 className="text-sm font-semibold">CloudFit Portal</h1>
            </header>
            <main className="flex-1 p-6">
              <Routes>
                <Route path="/" element={<Navigate to={defaultRoute[1].href} replace />} />
                {availableRoutes.map(([key, definition]) => {
                  const Component = pageComponents[key as Page]
                  return (
                    <Route
                      key={key}
                      path={definition.href}
                      element={<Component authUser={authUser} apiSession={apiSession} onLogout={handleLogout} />}
                    />
                  )
                })}
                <Route path="*" element={<Navigate to={defaultRoute[1].href} replace />} />
              </Routes>
            </main>
          </SidebarInset>
        </SidebarProvider>
      </TooltipProvider>
    </>
  )
}

export default App
